import React, {useContext, useState, useEffect, useCallback, useRef} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faFileExport,
  faSearch,
  faEdit,
  faTrash,
  faUserPlus,
  faEyeSlash,
  faEye,
  faArchive
} from "@fortawesome/free-solid-svg-icons";
import styled, {css} from "styled-components";

// Contexts
import {AuthContext} from "../../contexts/auth.js";
import {useToast} from "../../contexts/toast.js";

// Hooks
import useApi from "../../hooks/useApi.js";
import useMountedState from "../../hooks/useMountedState.js";

// Components
import BaseTable from "../../components/BaseTable.js";
import Pagination from "../../components/Pagination.js";
import Dropdown from "../../components/Dropdown.js";
import ModalDelete from "../../components/ModalDelete.js";
import ModalExport from "../../components/ModalExport.js";
import ModalManage from "./ModalManage.js";
import ModalAssign from "./ModalAssign.js";
import ModalViewAllUsers from "../general/ModalViewAllUsers.js";

// Style
import {bp} from "../../style/components/breakpoints.js";
import {colors, heroTheme, pad} from "../../style/components/variables.js";
import {
  Button,
  Heading,
  Text,
  Inline,
  TableWrapper,
  TableHeaderWrap,
  TableMenu,
  TableFooter,
  Search,
  SearchIcon,
  SearchWrapper,
  Small,
  Abbr,
  Pill
} from "../../style/components/general.js";

const TableUserContacts = () => {
  const isMounted = useMountedState();

  const {atLeast, roleCanAccessResource, departments} = useContext(AuthContext);
  const {addToast} = useToast();

  const [loadingUsers, setLoadingUsers] = useState(true);
  const [original, setOriginal] = useState([]);
  const [users, setUsers] = useState();
  // Edit Options
  const [edit, setEdit] = useState([]);
  const [selectedUser, setSelectedUser] = useState();
  // Search
  const [query, setQuery] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageTotal, setPageTotal] = useState(1);
  const [orderBy, setOrderBy] = useState("asc");
  const [groupBy, setGroupBy] = useState("fullName");
  const [limits, setLimits] = useState([]);
  const [limit, setLimit] = useState(10);
  // Modals
  const [showModalExport, setShowModalExport] = useState(false);
  const [showModalManage, setShowModalManage] = useState(false);
  const [showModalAssign, setShowModalAssign] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [showModalViewAll, setShowModalViewAll] = useState(false);

  const {api: apiUsers} = useApi("users");
  const {api: apiSuppressed} = useApi("users", {suppress: {success: true, error: true}});

  const editList = useRef(edit);

  const restore = useCallback(
    u => {
      const payload = {restore: true, ids: u.map(({publicId}) => publicId)};
      apiSuppressed.callPatch(null, payload).then(({status}) => {
        if (status === 200) {
          addToast(u.length === 1 ? "User restored" : "Users restored", "success");
          setUsers(null);
        } else
          addToast(u.length === 1 ? "Failed to restore user" : "Failed to restore users", "error");
      });
    },
    [apiSuppressed, addToast]
  );

  const multipleSelected = () => editList.current.length > 1;

  const formatUsers = useCallback(
    formattedUsers =>
      formattedUsers.map(user => {
        const {
          publicId,
          firstName,
          lastName,
          company,
          department,
          isInvited,
          isAccepted,
          isDeleted
        } = user;

        const formatted = {
          ...user,
          publicId: publicId,
          fullName: `${firstName} ${lastName}`,
          userCompanyId: company?.name,
          status: (
            <>
              {isInvited && isAccepted && !isDeleted && (
                <Pill color={heroTheme.success}>
                  <Small inverted>active</Small>
                </Pill>
              )}
              {isDeleted && (
                <Pill color={colors.gray}>
                  <Small inverted>
                    <Abbr title="User is archived">archived!</Abbr>
                  </Small>
                </Pill>
              )}
              {isInvited && !isAccepted && !isDeleted && (
                <Pill color={heroTheme.warning}>
                  <Small inverted>
                    <Abbr title="User has not accepted invitation">invited</Abbr>
                  </Small>
                </Pill>
              )}
              {!isInvited && !isDeleted && (
                <Pill color={colors.gray}>
                  <Small inverted>
                    <Abbr title="User has not been invited">contact</Abbr>
                  </Small>
                </Pill>
              )}
            </>
          ),
          options: (
            <Inline>
              <UserButton
                type="button"
                onClick={() => {
                  if (!multipleSelected()) {
                    setSelectedUser(user);
                    setShowModalManage(true);
                  }
                }}
                title="Edit User">
                <FontAwesomeIcon icon={faEdit} />
              </UserButton>
              {roleCanAccessResource("user", "delete") && (
                <Delete
                  type="button"
                  onClick={() => {
                    if (!multipleSelected()) {
                      if (isDeleted) restore([user]);
                      else {
                        setSelectedUser(user);
                        setShowModalDelete(true);
                      }
                    }
                  }}
                  title={isDeleted ? "Restore" : "Deleted"}>
                  {isDeleted ? "Restore" : <FontAwesomeIcon icon={faArchive} />}
                </Delete>
              )}
            </Inline>
          )
        };

        if (departments?.length > 0) formatted.userDepartmentId = department?.name?.toUpperCase();

        return formatted;
      }),
    [restore, roleCanAccessResource, departments]
  );

  const getUsers = useCallback(
    p => {
      setLoadingUsers(true);

      const filter = {Roles: ["external_contact"], showHidden};
      if (query) filter.Search = query;

      apiUsers
        .callGet("", {
          page: p || 1,
          orderBy,
          groupBy,
          limit,
          filter: JSON.stringify(filter)
        })
        .then(({status, data}) => {
          if (status === 200 && data) {
            setCurrentPage(data.page);
            setPageTotal(data.pages);
            setOriginal(data.users);
            const formatted = formatUsers(data.users);
            setUsers(formatted);

            // Page Limit
            const {total: dataTotal} = data;
            setTotal(dataTotal);
            let count = 10;
            const temp = [];
            while (count < dataTotal + 10 && count <= 30) {
              temp.push(count);
              count += 10;
            }

            setLimits(temp);
            setEdit([]);
            setLoadingUsers(false);
          }
        });
    },
    [query, apiUsers, orderBy, groupBy, limit, showHidden, formatUsers]
  );

  // Initial Load
  useEffect(() => {
    if (isMounted() && !users) getUsers();
  }, [isMounted, getUsers, users]);

  // Update users on search, filter, sort or limit
  useEffect(() => {
    if (isMounted()) {
      setUsers(undefined);
      setCurrentPage(1);
    }
  }, [isMounted, query, orderBy, groupBy, limit, showHidden]);

  useEffect(() => {
    editList.current = edit;

    if (edit.length === 1 && !selectedUser)
      original.map(user => {
        if (user.publicId === edit[0].publicId) setSelectedUser(user);
      });
    else if (edit.length > 1 && selectedUser) setSelectedUser(null);
  }, [edit, original, setSelectedUser, selectedUser]);

  const handleSearch = e => {
    setCurrentPage(1);
    setQuery(e.target.value);
  };

  const handleReload = () => {
    setCurrentPage(1);
    getUsers();
  };

  return (
    <>
      <UserTableWrapper disableButtons={edit.length > 1}>
        <TableHeaderWrap>
          <StyledInline>
            <Heading>Contacts</Heading>
            <InlineSpaced>
              <SearchWrapper>
                <SearchIcon>
                  <FontAwesomeIcon icon={faSearch} />
                </SearchIcon>
                <Search
                  name="search"
                  type="text"
                  placeholder="Search..."
                  onChange={e => handleSearch(e)}
                />
              </SearchWrapper>
              <TableMenu>
                {edit?.length > 0 ? (
                  <Inline>
                    {edit?.length > 0 && <Small>{edit.length}&nbsp;selected</Small>}
                    {edit?.length > 0 && !edit.some(user => user.isDeleted) && (
                      <Button
                        type="button"
                        title="Assign User(s)"
                        onClick={() => setShowModalAssign(true)}>
                        Assign Users(s)
                      </Button>
                    )}
                    {edit?.length === 1 &&
                      !edit[0].isDeleted &&
                      roleCanAccessResource("user", "update") && (
                        <Button
                          type="button"
                          title="Edit"
                          onClick={() => selectedUser && setShowModalManage(true)}>
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      )}
                    {roleCanAccessResource("user", "delete") && (
                      <GlobalDelete
                        type="button"
                        title={edit.length === 1 && edit[0].isDeleted ? "Restore" : "Delete"}
                        onClick={() => {
                          if (edit.length === 1 && edit[0].isDeleted) restore();
                          else setShowModalDelete(true);
                        }}>
                        <FontAwesomeIcon icon={faTrash} />
                      </GlobalDelete>
                    )}
                  </Inline>
                ) : (
                  <Inline>
                    {atLeast("admin") && (
                      <Button type="button" onClick={() => setShowHidden(!showHidden)}>
                        <FontAwesomeIcon icon={showHidden ? faEyeSlash : faEye} /> Archived
                      </Button>
                    )}
                    {roleCanAccessResource("user", "export") && users && users.length > 0 && (
                      <Button type="button" onClick={() => setShowModalExport(true)}>
                        <Abbr title="Export">
                          <FontAwesomeIcon icon={faFileExport} />
                        </Abbr>
                      </Button>
                    )}
                    {roleCanAccessResource("user", "create") && (
                      <Button
                        type="button"
                        aria-label="add user"
                        data-testid="openUserModal"
                        title="Add User"
                        onClick={() => {
                          setSelectedUser(null);
                          setEdit([]);
                          setShowModalManage(true);
                        }}>
                        <FontAwesomeIcon icon={faUserPlus} />
                      </Button>
                    )}
                  </Inline>
                )}
              </TableMenu>
            </InlineSpaced>
          </StyledInline>
        </TableHeaderWrap>
        <BaseTable
          headings={{
            publicId: {header: "", disabled: true},
            fullName: {header: "Name", disabled: false},
            userCompanyId: {header: "Company", disabled: false},
            userDepartmentId: {
              header: departments?.length > 0 ? "Contact Type" : "",
              disabled: false
            },
            status: {header: "Status", disabled: false},
            options: {header: "Options", disabled: true}
          }}
          data={users}
          loading={loadingUsers}
          orderBy={orderBy}
          setOrderBy={setOrderBy}
          groupBy={groupBy}
          setGroupBy={setGroupBy}
          items={edit}
          setItems={setEdit}
          showOptions
        />
        <TableFooter>
          {total > 0 && (
            <>
              <Inline>
                {total > 10 && limits.length > 0 && (
                  <PageLimit>
                    <Dropdown
                      options={limits}
                      selection={limit}
                      setSelection={selection => {
                        setLimit(selection);
                        setCurrentPage(1);
                      }}
                    />
                  </PageLimit>
                )}
                <Text quiet>
                  {limit <= 30 && pageTotal > 1 && limits.length > 0 && "per page, "}
                  <ModalViewLink
                    disabled={!total || total <= 10}
                    onClick={!total || total <= 10 ? undefined : () => setShowModalViewAll(true)}>
                    {total} total
                  </ModalViewLink>
                </Text>
              </Inline>
              {limit <= 30 && pageTotal > 1 && (
                <Pagination
                  current={currentPage}
                  setCurrent={setCurrentPage}
                  pageTotal={pageTotal}
                  updateData={() => getUsers(currentPage)}
                  loading={loadingUsers}
                />
              )}
            </>
          )}
        </TableFooter>
      </UserTableWrapper>

      {showModalExport && (
        <ModalExport
          visible={showModalExport}
          setVisible={setShowModalExport}
          allowRange={false}
          getExport={async exportParams =>
            apiUsers.callGet(null, {
              ...exportParams,
              filter: JSON.stringify({Roles: ["external_contact"], showHidden})
            })
          }
        />
      )}

      {edit && showModalAssign && (
        <ModalAssign
          visible={showModalAssign}
          setVisible={state => {
            if (!state) {
              setSelectedUser(null);
            }
            setShowModalAssign(state);
          }}
          users={edit}
          setUsers={setEdit}
        />
      )}

      {showModalManage && (
        <ModalManage
          visible={showModalManage}
          setVisible={state => {
            if (!state) {
              setSelectedUser(null);
            }
            setShowModalManage(state);
          }}
          user={selectedUser}
          updateTable={getUsers}
          invite={false}
        />
      )}

      {showModalDelete && (
        <ModalDelete
          visible={showModalDelete}
          setVisible={state => {
            if (!state) setSelectedUser(null);
            setShowModalDelete(state);
          }}
          title="Archive User(s)"
          prompt={`Are your sure you want to archive ${
            selectedUser ? `${selectedUser.firstName}'s account?` : "the selected accounts?"
          }`}
          action="Archive"
          confirmDelete={() => {
            const ids = selectedUser
              ? [selectedUser.publicId]
              : edit.map(current => current.publicId);
            apiUsers.callDelete(null, {data: {ids: ids}}).then(({status}) => {
              if (status === 200) {
                getUsers();
                setShowModalDelete(false);
                setEdit([]);
                setSelectedUser(null);
              }
            });
          }}
        />
      )}

      {showModalViewAll && (
        <ModalViewAllUsers
          visible={showModalViewAll}
          setVisible={setShowModalViewAll}
          reload={handleReload}
          defaultFilters={{Roles: ["external_contact"]}}
        />
      )}
    </>
  );
};

// Style Overrides
const PageLimit = styled.div`
  margin-right: ${pad}px;

  select {
    padding: ${pad / 2}px;
  }
`;

const StyledInline = styled(Inline)`
  gap: ${pad}px;
  display: block;
  width: 100%;

  ${bp(1)} {
    display: flex;
  }
`;

const InlineSpaced = styled(Inline)`
  justify-content: space-between;
  width: 100%;
`;

const UserButton = styled(Button)``;

const Delete = styled(UserButton)`
  background: ${({theme}) => theme.error};
`;

const GlobalDelete = styled(Button)`
  background: ${({theme}) => theme.error};
`;

const UserTableWrapper = styled(TableWrapper)`
  ${({disableButtons}) =>
    disableButtons &&
    css`
      ${UserButton} {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `}
`;

const ModalViewLink = styled.span`
  ${({disabled}) =>
    !disabled &&
    css`
      text-decoration: underline;
      cursor: pointer;
    `}
`;

export default TableUserContacts;
